'use client';

import { useEffect, useState } from 'react';
import salsify from 'salsify-experiences-sdk';

import { logger } from '@libs/core';

const SALSIFY_ORIGIN = 'salsify';
const SALSIFY_EMPTY_CONTENT_HEIGHT = 1;
const SALSIFY_HEIGHT_SETTLE_TIMEOUT = 2 * 1000;
const SALSIFY_LOAD_TIMEOUT = 15 * 1000;
const browserGlobal = globalThis as typeof globalThis & Pick<Window, 'flags'>;

export type UseSalsifyContentProps = {
    productId: string;
    salsifyClientId: string;
    salsifyIdType: string;
};

type SalsifyAvailability = {
    productId: string;
    found?: boolean;
};

export function useSalsifyContent({ salsifyClientId, salsifyIdType, productId }: UseSalsifyContentProps) {
    const [availability, setAvailability] = useState<SalsifyAvailability>({ productId });
    const salsifyFound = availability.productId === productId ? availability.found : undefined;

    useEffect(() => {
        let active = true;
        let heightSettleTimeout: ReturnType<typeof setTimeout> | undefined;
        let loadTimeout: ReturnType<typeof setTimeout> | undefined;
        let iframe: HTMLIFrameElement | null = null;
        const target = document.querySelector('#salsify-target') as HTMLDivElement | null;

        setAvailability({ productId });

        if (browserGlobal.flags?.IS_PERFORMANCE_MODE || !salsifyClientId || !salsifyIdType || !target) {
            setAvailability({ productId, found: false });
            return;
        }

        target.replaceChildren();

        const setUnavailable = () => {
            if (!active) return;

            clearTimeout(heightSettleTimeout);
            clearTimeout(loadTimeout);
            iframe?.removeEventListener('load', handleIframeLoad);
            target.replaceChildren();
            setAvailability({ productId, found: false });
        };

        const setAvailable = () => {
            if (!active) return;

            clearTimeout(heightSettleTimeout);
            clearTimeout(loadTimeout);
            setAvailability({ productId, found: true });
        };

        const settleIframeAvailability = () => {
            const height = Number(iframe?.height);

            if (Number.isFinite(height) && height > SALSIFY_EMPTY_CONTENT_HEIGHT) {
                setAvailable();
            } else {
                setUnavailable();
            }
        };

        const handleIframeLoad = () => {
            clearTimeout(loadTimeout);
            heightSettleTimeout = setTimeout(settleIframeAvailability, SALSIFY_HEIGHT_SETTLE_TIMEOUT);
        };

        const handleMessage = (event: MessageEvent) => {
            const height = Number(event.data?.height);

            if (
                !active ||
                !event.origin.includes(SALSIFY_ORIGIN) ||
                !Number.isFinite(height) ||
                height <= SALSIFY_EMPTY_CONTENT_HEIGHT
            )
                return;

            const renderedIframe = target.querySelector('#salsify-ec-iframe') as HTMLIFrameElement | null;

            if (renderedIframe) {
                renderedIframe.height = String(height);
                renderedIframe.style.height = `${height}px`;
            }
        };

        globalThis.addEventListener('message', handleMessage);

        const checkProduct = async () => {
            try {
                if (!salsify.initialized) {
                    salsify.init({
                        clientId: salsifyClientId,
                        enhancedContent: { idType: salsifyIdType },
                    });
                }

                const exists = await salsify.enhancedContent.exists(productId);

                if (!active) return;

                if (!exists) {
                    setUnavailable();
                    return;
                }

                salsify.enhancedContent.renderIframe(target, productId);

                if (!active) return;

                iframe = target.querySelector('#salsify-ec-iframe') as HTMLIFrameElement | null;

                if (!iframe) {
                    setUnavailable();
                    return;
                }

                iframe.addEventListener('load', handleIframeLoad, { once: true });
                loadTimeout = setTimeout(setUnavailable, SALSIFY_LOAD_TIMEOUT);
            } catch (error) {
                logger.error('[useSalsify] Failed to load Salsify content', error);
                setUnavailable();
            }
        };

        checkProduct();

        return () => {
            active = false;
            clearTimeout(heightSettleTimeout);
            clearTimeout(loadTimeout);
            iframe?.removeEventListener('load', handleIframeLoad);
            globalThis.removeEventListener('message', handleMessage);
        };
    }, [productId, salsifyClientId, salsifyIdType]);

    return salsifyFound;
}
